<template>
    <v-container>
        <v-row>
            <v-col>
                <v-breadcrumbs :items="breadcrumbs"></v-breadcrumbs>
                <v-btn :to="backRoute" prepend-icon="mdi-arrow-left-circle" variant="tonal">Tilbake til listen</v-btn>
            </v-col>
        </v-row>
        <v-row>
            <v-col>
                <v-card class="profile-whitelist-form">
                    <v-card-text>
                        <v-form ref="form" v-model="isFormValid" :class="{ loading: loading }" @submit.prevent="submit">
                            <phone-input v-model="phone" :phone-codes="phoneCountryCodes" />
                            <v-btn :disabled="submitDisabled" color="primary" type="submit">Submit</v-btn>
                        </v-form>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue';
import PhoneInput from '../shared/components/PhoneInput.vue';
import { useProfileWhitelistStore } from './profileWhitelistStore';
import { useCommonStore } from '@/features/shared/commonStore';
import { useRouter } from 'vue-router';
import { ProfileWhitelistRels } from './rels';
import { AlertTypes, Events, useEmitter } from '@/features/shared/useEmitter';
import { getErrors } from '../shared/ErrorHelper';
import { Phone, PhoneCountryCode } from '@/features/shared/models';

const store = useProfileWhitelistStore();
const commonStore = useCommonStore();
const router = useRouter();
const eventBus = useEmitter();

const isFormValid = ref<boolean | null>();
const form = ref<HTMLElement & { validate: () => Promise<{ valid: boolean; errors: any[] }> }>();
const phoneCountryCodes = ref<Array<PhoneCountryCode>>([]);
const loading = ref(false);
const backRoute = ref({ name: ProfileWhitelistRels.GetProfileWhitelists });
const breadcrumbs = [
    {
        title: 'Hviteliste',
        disabled: false,
        to: { name: ProfileWhitelistRels.GetProfileWhitelists }
    },
    {
        title: 'Ny hviteliste-oppføring (Telefon)'
    }
];

onMounted(async () => {
    store.clearCurrentProfileWhitelist();
    phoneCountryCodes.value = await commonStore.getCountryPhoneCodes();

    if (phoneCountryCodes.value.length > 0) {
        store.currentProfileWhitelist.phone = {
            countryCode: phoneCountryCodes.value[0].code,
            number: ''
        };
    }
});

const disabled = ref(false);

watch(isFormValid, newValue => {
    if (newValue === null) {
        disabled.value = false;
    } else {
        disabled.value = !newValue;
    }
});

const submitDisabled = ref(false);

const phone = computed({
    get: () => store.currentProfileWhitelist.phone || { countryCode: '', number: '' },
    set: (value: Phone) => {
        store.currentProfileWhitelist.phone = value;
    }
});

async function submit() {
    loading.value = true;
    const validation = await form.value?.validate();
    if (!validation?.valid) return;

    try {
        await store.createProfileWhitelistByPhone();
        loading.value = false;
        const alertContent = { type: AlertTypes.success, text: 'Hviteliste-oppføring lagret' };
        eventBus.emitter.emit(Events.alert, alertContent);
        await router.push({ name: ProfileWhitelistRels.GetProfileWhitelists });
    } catch (err: any) {
        loading.value = false;
        const data = err.response.data;
        const errors = getErrors(data);

        if (errors !== null) {
            eventBus.emitter.emit(Events.alert, errors);
        } else {
            throw err;
        }
    }
}
</script>

<style scoped>
.profile-whitelist-form {
    width: 100%;
    max-width: 700px;
}

.loading {
    opacity: 0.5;
}
</style>
